/**
 * Fishionist Preview Modal (v2) — a bigger look at the generated artwork.
 *
 * Deliberately additive. fishionist-personalizer.js owns the rule that a line
 * item cannot reach the cart without a print file, so nothing here writes cart
 * state: this file listens to the events that controller already broadcasts,
 * and its Try another button clicks the real one. If this script throws, the
 * page still sells posters.
 *
 * What it adds:
 *   - the frame, on the inline card and in the modal, following the Style variant
 *   - a room scene, sized to the Size variant
 *   - two-way sync with the Style variant picker
 *
 * The drawing itself lives in fsh-poster-view.js, shared with the homepage
 * Poster Builder. This file's job is to read Style/Size off <variant-selects>
 * and hand the answer to that module — the builder reads the same two facts off
 * its own buttons and calls the same code, so the two previews cannot drift.
 */
(function () {
  'use strict';

  var root = document.getElementById('fsh-personalizer');
  var modal = document.querySelector('[data-fsh-modal]');
  var view = window.FshPosterView;
  if (!root || !modal || !view) return;

  var DEFAULT_PLACEMENT = view.DEFAULT_PLACEMENT;

  var els = {
    preview: root.querySelector('[data-fsh-preview]'),
    inlineFrames: root.querySelectorAll('[data-fsh-inline-frame]'),
    srcRetry: root.querySelector('[data-fsh-retry]'),
    bgstage: root.querySelector('[data-fsh-bgstage]'),
    bgstageBg: root.querySelector('[data-fsh-bgstage-bg]'),
    bgpicker: root.querySelector('[data-fsh-bgpicker]'),
    bgslot: modal.querySelector('[data-fsh-bgslot]'),
    modalArt: modal.querySelector('[data-fsh-modal-art]'),
    modalFrame: modal.querySelector('[data-fsh-modal-frame]'),
    cards: modal.querySelector('[data-fsh-stylecards]'),
    cardThumbs: modal.querySelectorAll('[data-fsh-finish-thumb]'),
    room: modal.querySelector('[data-fsh-room]'),
    roomPicker: modal.querySelector('[data-fsh-roompicker]'),
    scaleNote: modal.querySelector('[data-fsh-scale-note]'),
    tabs: modal.querySelectorAll('[data-fsh-tab]'),
    panes: modal.querySelectorAll('[data-fsh-pane]'),
    modalRetry: modal.querySelector('[data-fsh-modal-retry]'),
  };

  // What is currently on screen. `url` is always the watermarked preview — the
  // clean print file must never reach the DOM or it can simply be right-clicked.
  var art = {
    mode: 'plain', // 'plain' | 'background'
    composed: true, // a background poster is two layers until /compose runs
    url: null,
    // The cut-out subject on its own. Kept apart from `url` because composing
    // overwrites `url` with the flat composite, and an adjustment has to be able
    // to go back to drawing the two layers.
    subjectUrl: null,
    bgUrl: null,
    placement: Object.assign({}, DEFAULT_PLACEMENT),
  };

  var rooms = view.readRooms();
  var selectedRoom = 0;
  var roomsBuilt = false;
  var activeTab = 'poster';
  var baseFrameW = 3.75; // read from CSS at init

  // ---- Small helpers ---------------------------------------------------------

  var el = view.el;

  // ---- Variant picker --------------------------------------------------------
  // The picker is rendered as radios (picker_type "button") but a merchant can
  // switch it to a dropdown in the editor, so both shapes are handled.

  function optionInputs(prefix) {
    var vs = document.querySelector('variant-selects');
    if (!vs) return [];
    return Array.prototype.filter.call(vs.querySelectorAll('input[type="radio"]'), function (i) {
      return i.name.indexOf(prefix + '-') === 0;
    });
  }

  function optionSelect(prefix) {
    var vs = document.querySelector('variant-selects');
    if (!vs) return null;
    return Array.prototype.filter.call(vs.querySelectorAll('select'), function (s) {
      return s.name.indexOf(prefix + '-') === 0;
    })[0] || null;
  }

  function getOptionValue(prefix) {
    var checked = optionInputs(prefix).filter(function (i) {
      return i.checked;
    })[0];
    if (checked) return checked.value;
    var sel = optionSelect(prefix);
    return sel ? sel.value : null;
  }

  // Dawn captures the changed input's id while the change event is still being
  // dispatched (global.js:1070-1077) and, once the section has re-rendered,
  // runs `document.querySelector('#'+id).focus()` (product-info.js:128-129).
  // Focusing an element that is off-screen scrolls the page to it, which drags
  // the customer from the panel up to the variant picker.
  //
  // So the id is hidden for exactly the length of the dispatch. Dawn memorises
  // an id that matches nothing, its later lookup returns null, and the optional
  // chaining makes the focus a no-op. Everything else — price, variant id,
  // availability — is untouched, because only the focus step reads the id.
  //
  // This replaces two earlier attempts that both treated the symptom: putting
  // the scroll back afterwards (still showed a frame at the wrong offset) and
  // forcing focus({preventScroll:true}) inside a timed window (the window
  // expired before the response arrived on a real phone connection).
  function dispatchSilently(node) {
    var realId = node.id;
    if (realId) node.id = realId + '--fsh-no-focus';
    try {
      node.dispatchEvent(new Event('change', { bubbles: true }));
    } finally {
      if (realId) node.id = realId;
    }
  }

  function setOptionValue(prefix, value) {
    var input = optionInputs(prefix).filter(function (i) {
      return i.value === value;
    })[0];
    if (input) {
      if (input.checked) return;
      input.checked = true;
      // <variant-selects> listens for change on itself; this is the same signal
      // a real click produces, so price and variant id update normally.
      dispatchSilently(input);
      return;
    }
    var sel = optionSelect(prefix);
    if (sel && sel.value !== value) {
      sel.value = value;
      dispatchSilently(sel);
    }
  }

  // Nothing here touches the page's scroll position. An earlier version pinned
  // it for a few seconds after a finish card was tapped, as insurance against
  // causes we had not identified — but that also blocked the customer's own
  // scrolling during the window, which is worse than the problem it guarded
  // against. dispatchSilently removes the cause instead, so there is nothing
  // left to defend against.

  // ---- Frame state -----------------------------------------------------------

  function isFramed() {
    return view.isFramedValue(getOptionValue('Style'));
  }

  // Every Size value the product offers, whichever shape the picker takes.
  function allSizeLabels() {
    var all = optionInputs('Size').map(function (i) {
      return i.value;
    });
    var sel = optionSelect('Size');
    if (sel) {
      Array.prototype.forEach.call(sel.options, function (o) {
        all.push(o.value);
      });
    }
    return all;
  }

  function posterScale() {
    return view.posterScale(getOptionValue('Size'), allSizeLabels());
  }

  // ---- Building the artwork --------------------------------------------------

  // One artwork renderer for every surface. A background poster that has not
  // been composed yet is still two layers, laid out exactly the way
  // buildPreview() in artwork-drawer.js does it so the two agree.
  function fillArt(box) {
    view.fillArt(box, art);
  }

  // The finish cards show the same artwork at thumbnail size, so the customer
  // compares the two finishes on their own picture rather than a generic swatch.
  // The card already says what it is; a third "Your custom poster" in the same
  // button is noise to a screen reader, so the alt is dropped.
  function fillFinishThumbs() {
    Array.prototype.forEach.call(els.cardThumbs, function (box) {
      view.fillArt(box, Object.assign({}, art, { alt: '' }));
    });
  }

  // ---- Frame state -----------------------------------------------------------

  function applyFrame() {
    var framed = isFramed();
    Array.prototype.forEach.call(els.inlineFrames, function (f) {
      f.classList.toggle('is-framed', framed);
    });
    if (els.modalFrame) els.modalFrame.classList.toggle('is-framed', framed);

    if (els.cards) {
      var current = getOptionValue('Style');
      Array.prototype.forEach.call(els.cards.children, function (b) {
        var on = b.dataset.fshStyle === current;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    var poster = modal.querySelector('.fsh-room__poster');
    if (poster) poster.classList.toggle('is-framed', framed);
  }

  // ---- Room scene ------------------------------------------------------------

  function buildRoom() {
    if (!els.room || !rooms.length) return;

    view.buildRoom(els.room, {
      art: art,
      framed: isFramed(),
      scale: posterScale(),
      room: rooms[selectedRoom] || rooms[0],
      baseFrameW: baseFrameW
    });

    if (els.scaleNote) {
      var size = getOptionValue('Size');
      els.scaleNote.textContent = size ? 'Shown at ' + size : '';
    }
    syncRoomChrome();
  }

  // The size caption and the room picker belong to the wall view only. They sit
  // outside the panes because the preview box is a fixed height with overflow
  // hidden — inside it they would be clipped — so their visibility is driven
  // from here instead. On the poster tab the artwork is drawn at one fixed
  // ratio whatever size is selected, so "Shown at 12x18IN" would be a lie.
  function syncRoomChrome() {
    var onRoom = activeTab === 'room';
    if (els.scaleNote) els.scaleNote.hidden = !onRoom || !els.scaleNote.textContent;
    if (els.roomPicker) els.roomPicker.hidden = !onRoom || rooms.length < 2;
  }

  function buildRoomPicker() {
    if (!els.roomPicker || rooms.length < 2) return;
    els.roomPicker.textContent = '';
    rooms.forEach(function (r, i) {
      var btn = el('button', 'fsh-modal__roomthumb' + (i === selectedRoom ? ' is-selected' : ''));
      btn.type = 'button';
      btn.title = r.label || '';
      var img = el('img');
      img.alt = r.label || '';
      img.src = r.thumb;
      btn.appendChild(img);
      btn.addEventListener('click', function () {
        selectedRoom = i;
        buildRoomPicker();
        buildRoom();
      });
      els.roomPicker.appendChild(btn);
    });
  }

  // ---- Modal open / close ----------------------------------------------------

  // The panel is not an overlay — it stands in for the old preview card, so it
  // is simply filled and revealed in place. There is nothing to dismiss.
  function showPanel() {
    if (!art.url) return;
    mountPoster();
    fillFinishThumbs();
    applyFrame();
    showTab('poster');
    var wasHidden = modal.hidden;
    modal.hidden = false;
    relayoutSubject();

    // Inline means the panel can land below the fold, and a customer who has
    // just waited a minute for their artwork should not have to hunt for it.
    // Only on the transition into view, so re-renders never yank the page.
    if (wasHidden) {
      requestAnimationFrame(function () {
        modal.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  // The controller positions the background subject from the stage's box, and
  // it listens for window resize to redo it (fishionist-personalizer.js:805).
  // While the panel was hidden that box measured zero, so its first attempt
  // bailed out — ask again once the panel actually has a size.
  function relayoutSubject() {
    if (art.mode !== 'background') return;
    requestAnimationFrame(function () {
      window.dispatchEvent(new Event('resize'));
    });
  }

  // A background product's poster is not an image we can redraw — it is the
  // live drag surface the controller owns. Adopt that element instead of
  // copying it, so dragging and pinch-zoom keep working inside the new layout.
  function mountPoster() {
    if (art.mode === 'background' && els.bgstage) {
      if (els.bgstage.parentElement !== els.modalFrame) {
        els.modalFrame.appendChild(els.bgstage);
      }
      els.modalArt.hidden = true;
      mountBgPicker();
      return;
    }
    els.modalArt.hidden = false;
    fillArt(els.modalArt);
  }

  // Same adoption trick as the stage. Left where it is, the picker would sit
  // stranded below the panel, controlling a poster it no longer sits next to —
  // the exact split this panel exists to remove. Products with one fixed
  // background keep it hidden, and then the slot hides with it.
  function mountBgPicker() {
    if (!els.bgpicker || !els.bgslot) return;
    if (els.bgpicker.parentElement !== els.bgslot) {
      els.bgslot.appendChild(els.bgpicker);
    }
    syncBgSlot();
  }

  function syncBgSlot() {
    if (!els.bgslot) return;
    els.bgslot.hidden = !els.bgpicker || els.bgpicker.hidden;
  }

  function showTab(name) {
    Array.prototype.forEach.call(els.tabs, function (t) {
      var on = t.dataset.fshTab === name;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    Array.prototype.forEach.call(els.panes, function (p) {
      p.classList.toggle('is-active', p.dataset.fshPane === name);
    });
    activeTab = name;
    // The wall view sizes itself to the photo, the poster view keeps its box.
    var box = modal.querySelector('.fsh-modal__preview');
    if (box) box.classList.toggle('is-room', name === 'room');
    syncRoomChrome();
    // Room photos are only fetched once the customer actually asks for them.
    if (name === 'room' && !roomsBuilt) {
      roomsBuilt = true;
      buildRoomPicker();
    }
    if (name === 'room') buildRoom();
    else relayoutSubject();
  }

  // ---- Wiring ----------------------------------------------------------------

  function init() {
    baseFrameW = view.readBaseFrameW(baseFrameW);

    Array.prototype.forEach.call(els.tabs, function (t) {
      t.addEventListener('click', function () {
        showTab(t.dataset.fshTab);
      });
    });

    if (els.cards) {
      Array.prototype.forEach.call(els.cards.children, function (b) {
        b.addEventListener('click', function () {
          setOptionValue('Style', b.dataset.fshStyle);
          // Dawn re-renders variant-selects asynchronously; paint the card now
          // so the choice feels instant, then the change listener confirms it.
          applyFrame();
          if (!modal.hidden) buildRoom();
        });
      });
    }

    // Proxy, never re-implement: this clicks the controller's own button.
    if (els.modalRetry && els.srcRetry) {
      els.modalRetry.addEventListener('click', function () {
        els.srcRetry.click();
      });
    }

    // Delegated on document on purpose: product-info.js replaces the whole
    // <variant-selects> element on every variant change, so a listener bound
    // directly to a radio would be thrown away the first time it fired.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.name) return;
      if (t.name.indexOf('Style-') === 0) {
        applyFrame();
        if (!modal.hidden) buildRoom();
      } else if (t.name.indexOf('Size-') === 0) {
        if (!modal.hidden) buildRoom();
      }
    });

    // ---- Artwork lifecycle ----

    document.addEventListener('fsh:artwork-generated', function (e) {
      var r = (e.detail && e.detail.response) || {};
      if (r.mode === 'background' && r.subject_preview_url) {
        art.mode = 'background';
        art.composed = false;
        art.url = r.subject_preview_url;
        art.subjectUrl = r.subject_preview_url;
        art.bgUrl = els.bgstageBg ? els.bgstageBg.src : null;
        art.placement = Object.assign({}, DEFAULT_PLACEMENT);
      } else {
        art.mode = 'plain';
        art.composed = true;
        art.url = r.preview_url || r.output_url || null;
        art.bgUrl = null;
      }
      // Deferred a frame so the controller has finished revealing its card.
      requestAnimationFrame(showPanel);
    });

    document.addEventListener('fsh:artwork-composed', function (e) {
      var d = e.detail || {};
      art.composed = true;
      art.url = d.previewUrl || art.url;
      syncInlineArt();
    });

    document.addEventListener('fsh:artwork-placement', function (e) {
      var d = e.detail || {};
      if (d.placement) art.placement = Object.assign({}, d.placement);
      // A background switch also arrives here, so re-read the live layer.
      if (els.bgstageBg) art.bgUrl = els.bgstageBg.src;

      // Any adjustment puts the poster back to two live layers. Composing now
      // happens at Add to Cart, so a composite only ever exists after the
      // customer has left — which means an adjustment can always drop it.
      // Without this the wall view and the finish cards would keep painting a
      // stale composite and silently ignore what the customer just did; the
      // poster tab hid that, because it shows the live stage, not a redraw.
      if (art.mode === 'background' && art.subjectUrl) {
        art.composed = false;
        art.url = art.subjectUrl;
      }

      if (!modal.hidden) {
        fillArt(els.modalArt);
        // The finish cards draw the artwork too, so they go stale on a
        // background switch unless they are redrawn with everything else.
        fillFinishThumbs();
        buildRoom();
      }
    });

    // "Try another image" hides the preview card. There is no event for it, so
    // watch the attribute the controller toggles and drop our state with it.
    if (els.preview) {
      new MutationObserver(function () {
        if (els.preview.hasAttribute('hidden')) {
          art.url = null;
          modal.hidden = true;
        }
      }).observe(els.preview, { attributes: true, attributeFilter: ['hidden'] });
    }

    // The picker is filled and revealed by the controller once /config lands,
    // which can be after this runs and again if it is ever hidden.
    if (els.bgpicker) {
      new MutationObserver(syncBgSlot).observe(els.bgpicker, {
        attributes: true,
        attributeFilter: ['hidden'],
      });
    }

    applyFrame();
  }

  // The inline card paints itself; we only keep the modal in step with it.
  function syncInlineArt() {
    applyFrame();
    fillFinishThumbs();
    if (!modal.hidden) {
      mountPoster();
      buildRoom();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
